/**
 * Shared component for displaying active filters in tables.
 *
 * This component renders a filter bar that shows:
 * - Count of active filters
 * - "Clear all" button to reset all filters
 * - Individual filter tags for each active filter value
 * - Date range tag when date filters are active
 *
 * Used by: VariantCheck (variant search), ProbandsSamplesPage (sample search)
 *
 * @example
 * <ActiveFilterBar
 *   columnFilters={columnFilters}
 *   dateFrom={createdDateFrom}
 *   dateTo={createdDateTo}
 *   dateLabel="Creation date"
 *   onFilterChange={handleFilterChange}
 *   onClearDateRange={() => { setCreatedDateFrom(''); setCreatedDateTo(''); }}
 *   onClearAll={handleClearAllFilters}
 *   getActiveFiltersCount={getActiveFiltersCount}
 *   classNamePrefix="l-variant-chk"
 * />
 */
import { Button, Tag } from 'antd';
import React from 'react';

// Interface for filter state: object with string keys and string array values
// Example: { classification: ['Pathogenic', 'Benign'], creator: ['john'] }
export interface FilterState {
  [key: string]: string[];
}

// Props interface for the ActiveFilterBar component
export interface ActiveFilterBarProps {
  columnFilters: FilterState; // Current filter selections from the hook
  dateFrom?: string; // Current "from" date value (optional)
  dateTo?: string; // Current "to" date value (optional)
  dateLabel?: string; // Label for the date range tag (default: 'Date')
  searchTerm?: string; // Current search term (optional)
  onFilterChange: (key: string, values: string[]) => void; // Function to update filter values
  onClearDateRange?: () => void; // Function to clear date range (only needed when dateFrom/dateTo are used)
  onClearSearch?: () => void; // Function to clear search term (only needed when searchTerm is used)
  onClearAll: () => void; // Function to clear all filters
  getActiveFiltersCount: () => number; // Function to calculate active filter count
  classNamePrefix?: string; // CSS class prefix for styling (default: 'active-filter-bar')
}

export const ActiveFilterBar: React.FC<ActiveFilterBarProps> = ({
  columnFilters,
  dateFrom,
  dateTo,
  dateLabel = 'Date',
  searchTerm,
  onFilterChange,
  onClearDateRange,
  onClearSearch,
  onClearAll,
  getActiveFiltersCount,
  classNamePrefix = 'active-filter-bar',
}) => {
  // Get the total count of active filters
  const activeFiltersCount = getActiveFiltersCount();
  const hasSearchTerm = Boolean(searchTerm && searchTerm.trim());

  // Don't render anything if there are no active filters and no search term
  if (activeFiltersCount === 0 && !hasSearchTerm) {
    return null;
  }

  return (
    <div className={`${classNamePrefix}__filters-bar`}>
      {/* Left side: filter count and clear all button */}
      <div className={`${classNamePrefix}__filters-info`}>
        <span className={`${classNamePrefix}__filters-count`}>
          {activeFiltersCount} active filter
          {activeFiltersCount !== 1 ? 's' : ''}
        </span>
        <Button
          type="link"
          size="small"
          onClick={onClearAll}
          className={`${classNamePrefix}__clear-all-btn`}
        >
          Clear all
        </Button>
      </div>
      {/* Right side: individual filter tags */}
      <div className={`${classNamePrefix}__active-filters`}>
        {/* Render search term tag if search is active */}
        {hasSearchTerm && (
          <Tag
            key="search"
            closable
            onClose={onClearSearch}
            className={`${classNamePrefix}__filter-tag`}
          >
            Search: {searchTerm}
          </Tag>
        )}
        {/* Render a tag for each active filter value */}
        {Object.entries(columnFilters).map(([key, values]) =>
          values.map((value) => (
            <Tag
              key={`${key}-${value}`}
              closable
              onClose={() => {
                // Remove the clicked value from the filter
                const newValues = values.filter((v) => v !== value);
                onFilterChange(key, newValues);
              }}
              className={`${classNamePrefix}__filter-tag`}
            >
              {value}
            </Tag>
          )),
        )}
        {/* Render date range tag if date filters are active */}
        {(dateFrom || dateTo) && (
          <Tag
            key="date-range"
            closable
            onClose={onClearDateRange}
            className={`${classNamePrefix}__filter-tag`}
          >
            {dateLabel}: {dateFrom || '...'} - {dateTo || '...'}
          </Tag>
        )}
      </div>
    </div>
  );
};
